import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

interface ConfirmSupplierOrderInput {
    supplierOrderId: string;
}

interface ConfirmSupplierOrderResponse {
    confirmedParts: number;
}

const confirmSupplierOrder = async (
    data: ConfirmSupplierOrderInput,
    sessionId: string | null
): Promise<ConfirmSupplierOrderResponse> => {
    const res = await axios.post(`${API_URL}/order/supplier-order/confirm`, data, {
        headers: {
            Authorization: sessionId
        }
    });
    return res.data;
};

/**
 * Manually mark a stuck supplier order confirmed — the escape hatch for POs the
 * vendor confirmed somewhere the marketplace never sees (e.g. over the phone).
 * Applies the same transition the automated confirmation flow would have: the PO
 * and its in-flight parts move to Confirmed and re-keying can start.
 */
export const useConfirmSupplierOrder = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ConfirmSupplierOrderInput) => confirmSupplierOrder(data, sessionContext?.sessionId ?? null),
        onSuccess: () => {
            // Broad prefix match for open order views; mirrors useAcceptSupplierChanges.
            queryClient.invalidateQueries({ queryKey: ['/order/detail'] });
            queryClient.invalidateQueries({ queryKey: ['/order/activity-logs'] });
            queryClient.invalidateQueries({ queryKey: ['/order/drawer'] });
            queryClient.invalidateQueries({ queryKey: ['order-confirmation'] });
            queryClient.invalidateQueries({ queryKey: ['quoteData'] });
        },
        onError: (error) => {
            console.error('Failed to confirm supplier order:', error);
        }
    });
};
